import { ThemeProvider } from '@front10/base-elements';
import * as React from 'react';
import { getAirlines } from '../../../_integrations/everymundo/airlines';
import { getBlackoutDates } from '../../../_integrations/everymundo/blackoutDates';
import { getAirports } from '../../../_integrations/everymundo/airports';
import {
  useTrackingChange,
  VerticalBookingActions
} from '../../../_integrations/everymundo/tracking/booking';
import withCommonEMContainer from '../_commons/CommonEMContainer';
import { EmotionCache } from '../_commons/emotionCache';
import { AirportMarketFilter } from './airports';
import { BookingPopupContainer as BookingPopup } from './BookingPopup';
import { DatePickerLabelsProvider } from './DatePickerLabelsProvider';
import { prepareAirportForMarket } from './utils';
import { OriginDestinationLogic } from '../_commons/filters';

function getPubSub() {
  if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    return window.EM.utils.PubSub;
  }
  return undefined;
}

function Container(props) {
  let prePopulation = props.prePopulation;
  const track = useTrackingChange();

  const fetchAirlines = React.useCallback(
    ({ selectedOrigin, selectedDestination }) =>
      getAirlines(
        prePopulation.airlines,
        selectedOrigin,
        selectedDestination,
        props.integrationSettings,
        props.meta,
        props.pageContext
      ),
    [prePopulation, props.integrationSettings, props.meta, props.pageContext]
  );

  const fetchBlackoutDates = React.useCallback(
    ({ selectedOrigin, selectedDestination, isRoundtrip }) => {
      return getBlackoutDates(
        selectedOrigin,
        selectedDestination,
        isRoundtrip,
        props.integrationSettings,
        props.meta,
        props.pageContext
      );
    },
    [props.integrationSettings, props.meta, props.pageContext]
  );

  const fetchAirports = React.useCallback(
    ({ airportType, selectedOrigin, filterByMarket = AirportMarketFilter.NONE }) => {
      const airport = prepareAirportForMarket(prePopulation.airport, airportType, filterByMarket);

      const { originDestinationLogic = 'same' } = props;
      const avoidCall = originDestinationLogic === OriginDestinationLogic.SAME;

      if (avoidCall && airportType === 'DESTINATION' && !selectedOrigin) {
        return Promise.resolve([]);
      }

      return getAirports(
        {
          ...airport,
          airlineIataCode: prePopulation.airlineIataCode,
          language: prePopulation.languageCode,
          routeOption: {
            airportType,
            selectedOrigin
          }
        },
        props.integrationSettings,
        props.meta,
        props.pageContext
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      prePopulation,
      props.integrationSettings,
      props.meta,
      props.pageContext,
      props.originDestinationLogic
    ]
  );
  return (
    <EmotionCache direction={props.direction}>
      <ThemeProvider theme={props.theme.designTokens || props.theme}>
        <DatePickerLabelsProvider
          localizationData={props.localizationData}
          translate={props.translate}
          weekdayFormat={
            props?.componentsConfiguration?.dateSelector?.weekdayFormat
              ? props?.componentsConfiguration?.dateSelector?.weekdayFormat
              : 'min'
          }
        >
          <BookingPopup
            onSubmit={(data) => {
              track(VerticalBookingActions.SEARCH_FLIGHTS, {
                ...data,
                businessRules: props.businessRules
              });
            }}
            fetchAirports={fetchAirports}
            fetchAirlines={fetchAirlines}
            fetchBlackoutDates={fetchBlackoutDates}
            onOriginChange={(data) => track(VerticalBookingActions.CHANGE_ORIGIN, data)}
            onAirlineChange={(data) => track(VerticalBookingActions.CHANGE_AIRLINE, data)}
            onJourneyTypeChange={(data) => track(VerticalBookingActions.CHANGE_JOURNEY_TYPE, data)}
            onDestinationChange={(data) => track(VerticalBookingActions.CHANGE_DESTINATION, data)}
            onDepartureDateChange={(data) =>
              track(VerticalBookingActions.CHANGE_DEPARTURE_DATE, data)
            }
            onReturnDateChange={(data) => track(VerticalBookingActions.CHANGE_RETURN_DATE, data)}
            getPubSub={getPubSub}
            {...props}
          />
        </DatePickerLabelsProvider>
      </ThemeProvider>
    </EmotionCache>
  );
}

export const BookingPopupContainer = withCommonEMContainer(Container);
